import { CapacitorSQLite, SQLiteConnection, type SQLiteDBConnection } from '@capacitor-community/sqlite';

import type { Store, Where } from './port';
import type { TableName } from './rows';
import { AUFSTIEGE, SCHEMA_VERSION } from './schema';
import { merkeSql } from './sqlLog';

/**
 * Die Ablage auf dem Gerät.
 *
 * Migrationen laufen über `addUpgradeStatement` des Plugins — dessen eigener Weg, kein
 * selbstgebautes Werk. Fassung 1 ist `DDL` aus `schema.ts`, jede weitere ein eigener Schritt
 * (`AUFSTIEG_2`, …); das Schema entsteht als Kette, nicht aus `DDL` allein.
 */

const DB_NAME = 'osce';

/**
 * Spaltennamen können in SQL **nicht** als Platzhalter gebunden werden; sie wandern in den
 * Text. Deshalb wird jeder Name geprüft, bevor er dort landet. Werte gehen immer über `?`.
 */
const SPALTE = /^[A-Za-z][A-Za-z0-9]*$/;
const prüfeSpalte = (name: string) => {
  if (!SPALTE.test(name)) throw new Error('Unzulässiger Spaltenname: ' + name);
  return name;
};

/**
 * Echtes Upsert statt `INSERT OR REPLACE`: Letzteres setzt SQLite intern als
 * DELETE-dann-INSERT um. Mit `PRAGMA foreign_keys = ON` (siehe `open()`) und den Kaskaden aus
 * `schema.ts` (z. B. `Exam → ExamPart → ExamItemContainer → ExamItem → ExamQuestion →
 * ExamAnswer`) würde ein einfaches „Exam-Namen ändern" über `put('Exam', […])` die komplette
 * abhängige Prüfungsstruktur löschen, bevor die eine Zeile neu eingefügt wird — stiller
 * Datenverlust, ohne dass am Gerät ein Debugger hinge. `ON CONFLICT(id) DO UPDATE` ändert nur
 * die eine Zeile, keine Fremdschlüssel-Kaskade läuft an.
 *
 * Trägt eine Zeile außer `id` kein weiteres Feld, wäre das `SET` leer und das SQL ungültig —
 * dann `DO NOTHING`, denn ohne Feld gibt es nichts zu setzen.
 */
export function buildInsert(table: TableName, row: Record<string, unknown>) {
  const felder = Object.keys(row).filter((f) => row[f] !== undefined).map(prüfeSpalte);
  const ohneId = felder.filter((f) => f !== 'id');
  const konflikt =
    ohneId.length > 0
      ? 'DO UPDATE SET ' + ohneId.map((f) => f + ' = excluded.' + f).join(',')
      : 'DO NOTHING';
  const sql =
    'INSERT INTO ' + table + ' (' + felder.join(',') + ') VALUES (' +
    felder.map(() => '?').join(',') + ') ON CONFLICT(id) ' + konflikt;
  return { sql, werte: felder.map((f) => row[f] ?? null) };
}

export function buildWhere(where: Where | undefined) {
  const felder = Object.keys(where ?? {}).map(prüfeSpalte);
  if (felder.length === 0) return { sql: '', werte: [] as unknown[] };
  const teile = felder.map((f) => (where![f] === null ? f + ' IS NULL' : f + ' = ?'));
  const werte = felder.map((f) => where![f]).filter((w) => w !== null);
  return { sql: ' WHERE ' + teile.join(' AND '), werte };
}

export function createSqliteStore(): Store {
  const verbindung = new SQLiteConnection(CapacitorSQLite);
  let db: SQLiteDBConnection | null = null;
  /** Wächter gegen Verschachtelung — SQLite-Transaktionen sind nicht reentrant. */
  let inTx = false;

  const offen = (): SQLiteDBConnection => {
    if (!db) throw new Error('Ablage nicht geöffnet — open() fehlt.');
    return db;
  };

  const tx = async <T>(fn: (t: Store) => Promise<T>): Promise<T> => {
    // Wie beim Browser-Adapter ist tx() nicht verschachtelbar. SQLite hätte mit SAVEPOINT
    // zwar echte Mittel dafür — die gäbe es hier aber ungenutzt und ungeprüft: die Store-
    // Schnittstelle verspricht Verschachtelung nirgends, also wird sie nicht heimlich
    // unterstützt. Ein zweiter Aufruf während einer laufenden Transaktion wirft, statt
    // BEGIN doppelt abzusetzen und den Zustand von SQLite und Wächter auseinanderlaufen zu
    // lassen.
    if (inTx) {
      throw new Error('tx() ist nicht verschachtelbar — eine Transaktion läuft bereits');
    }
    inTx = true;
    try {
      await offen().beginTransaction();
      merkeSql('BEGIN');
      try {
        const ergebnis = await fn(ablage);
        await offen().commitTransaction();
        merkeSql('COMMIT');
        return ergebnis;
      } catch (fehler) {
        try {
          await offen().rollbackTransaction();
          merkeSql('ROLLBACK');
        } catch (rollbackFehler) {
          // Wie in web.ts: der ursprüngliche Fehler bleibt maßgeblich und wird geworfen; ein
          // Fehler beim Zurückrollen hängt nur als Begleitinformation an, statt ihn zu
          // verdrängen.
          if (typeof fehler === 'object' && fehler !== null) {
            (fehler as { restoreErrors?: unknown[] }).restoreErrors = [rollbackFehler];
          }
        }
        throw fehler;
      }
    } finally {
      inTx = false;
    }
  };

  /**
   * Schreibt eine Zeilenliste innerhalb einer bereits offenen manuellen Transaktion — deshalb
   * hier immer `transaction: false` an `run()`. Ohne das versucht das Plugin, jede Zeile in
   * einen eigenen impliziten Auto-Commit-Rahmen zu wickeln und stößt dabei auf die schon
   * laufende Transaktion.
   */
  const stapelSchreiben = async (table: TableName, rows: Record<string, unknown>[]) => {
    for (const satz of rows) {
      const { sql, werte } = buildInsert(table, satz);
      await offen().run(sql, werte, false);
      merkeSql(sql, werte);
    }
  };

  const ablage: Store = {
    async open() {
      /**
       * ⚠️ **Beide Fassungen müssen hier stehen, nicht nur die neueste.** Das Plugin wendet
       * die Schritte an, die zwischen der vorgefundenen und der verlangten Fassung liegen;
       * fehlte Fassung 1, käme ein Gerät mit einer Fassung-1-Ablage — jedes, auf dem schon
       * bewertet wurde — nie bei 2 an.
       */
      await CapacitorSQLite.addUpgradeStatement({
        database: DB_NAME,
        upgrade: AUFSTIEGE.map((stufe) => ({
          toVersion: stufe.bis,
          statements: [...stufe.saetze],
        })),
      });
      db = await verbindung.createConnection(DB_NAME, false, 'no-encryption', SCHEMA_VERSION, false);
      await db.open();
      await db.execute('PRAGMA foreign_keys = ON');
    },

    async put(table, rows) {
      // Alles oder nichts je Aufruf, wie im Browser-Adapter: Läuft schon eine Transaktion
      // (Aufruf aus fn() innerhalb von tx()), wird sie mitbenutzt; sonst spannt put() selbst
      // eine auf, damit ein Abbruch mitten im Stapel keine halb geschriebenen Zeilen
      // hinterlässt — gerade nativ fallen CHECK-Constraints aus schema.ts hart auf.
      const stapel = rows as unknown as Record<string, unknown>[];
      if (inTx) {
        await stapelSchreiben(table, stapel);
      } else {
        await tx(() => stapelSchreiben(table, stapel));
      }
    },

    async all<T>(table: TableName, where?: Where) {
      const { sql, werte } = buildWhere(where);
      const ergebnis = await offen().query('SELECT * FROM ' + table + sql, werte);
      return (ergebnis.values ?? []) as T[];
    },

    async remove(table, where) {
      const { sql, werte } = buildWhere(where);
      // Siehe stapelSchreiben(): kein impliziter zweiter BEGIN in eine laufende Transaktion.
      await offen().run('DELETE FROM ' + table + sql, werte, !inTx);
      merkeSql('DELETE FROM ' + table + sql, werte);
    },

    tx,

    async close() {
      await db?.close();
      await verbindung.closeConnection(DB_NAME, false);
      db = null;
      inTx = false;
    },
  };
  return ablage;
}
